import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import type { Driver, Order, Vehicle } from '@hero/schema';
import { getObjectId, isIdMatch } from '@hero/schema';
import type { OrderUpdateAPI } from '@hero/api';
import {
  useAssignOrderApi,
  useBestMatchVehiclesApi,
  useDriverListApi,
  useVehicleListApi,
} from '@hero/api-hooks';
import {
  AlertCircleIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  DriverIcon,
  PlusIcon,
  SearchIcon,
  TruckIcon,
} from '@hero/web-icons';
import { cn } from '@hero/web-lib';

import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '../../../ui/Sheet/Sheet';
import { Button } from '../../../ui/Button/Button';
import Spinner from '../../../app/Spinner/Spinner';
import {
  AvailabilityChip,
  driverStatusChip,
  formatPlateForDisplay,
  vehicleStatusChip,
} from '../../../app/Select/SelectAsync/selectorEntityRowUtils';
import { DriverCreateModal } from '../../Driver/Modal';
import { VehicleEditModal } from '../../Vehicle/Modal/VehicleEditModal';

export interface OrderDriverPickerDrawerProps {
  /** Order being assigned to. `null` keeps the drawer mounted but inert. */
  order: Order | null;
  /** Whether the drawer is open. */
  isOpen: boolean;
  /** Close handler — fires on overlay click / esc / explicit cancel. */
  onClose: () => void;
  /** Fired after a successful PATCH lands. */
  onAssigned?: (order: Order) => void;
}

/**
 * Driver-assign wizard. Three deliberate steps in a single Sheet:
 *
 *   1. **driver**  — pick a driver (search + create modal trigger).
 *   2. **vehicle** — pick or create a vehicle. Skipped automatically
 *                    when the picked driver already has a bound
 *                    vehicle (the backend infers it).
 *   3. **confirm** — review card on first-assign, swap card
 *                    ("X → Y") on reassign. Busy badges + warning
 *                    when the picked driver/vehicle is currently on
 *                    another order. The `useAssignOrderApi` PATCH
 *                    fires here, NOT on driver tap.
 *
 * Why a wizard instead of tap-to-commit:
 *
 *   - The platform now allows assigning an order to an already-busy
 *     driver (Phase "schedulable busy drivers"). The confirm step
 *     surfaces that fact deliberately rather than surprising the
 *     user post-PATCH. See `apps/backend/src/app/order/README.md`.
 *   - Reassign needs a clear "you're replacing X with Y" affordance.
 *   - Vehicle pick used to open a SECOND `Sheet` on top of the
 *     driver one, on top of the order drawer (3-deep stacking, ESC
 *     and focus-trap pain). The wizard keeps everything inside one
 *     Sheet.
 *
 * `OrderVehiclePickerDrawer` still ships standalone because
 * `VehicleDetails` uses it for the vehicle-only change flow.
 */
export function OrderDriverPickerDrawer({
  order,
  isOpen,
  onClose,
  onAssigned,
}: OrderDriverPickerDrawerProps) {
  const { t } = useTranslation(['m-order', 'm-driver', 'm-vehicle', 'c-main']);

  type WizardStep = 'driver' | 'vehicle' | 'confirm';
  const [step, setStep] = useState<WizardStep>('driver');
  const [pickedDriver, setPickedDriver] = useState<Driver | undefined>(undefined);
  const [pickedVehicle, setPickedVehicle] = useState<Vehicle | undefined>(undefined);
  const [isCreateDriverOpen, setIsCreateDriverOpen] = useState(false);
  const [isCreateVehicleOpen, setIsCreateVehicleOpen] = useState(false);

  // Fully reset on close so reopening lands on Step 1 with no
  // stale picks / search. The order-id dep handles "drawer reopened
  // for a different order in the same parent surface".
  useEffect(() => {
    if (!isOpen) {
      setStep('driver');
      setPickedDriver(undefined);
      setPickedVehicle(undefined);
      setIsCreateDriverOpen(false);
      setIsCreateVehicleOpen(false);
    }
  }, [isOpen, order?._id]);

  const { mutate, isPending } = useAssignOrderApi({
    onSuccess: result => {
      const isReassign = !!order?.driver?._id;
      toast.success(
        t(
          isReassign
            ? 'm-order:drawer.driver_picker.confirm.replaced_toast'
            : 'm-order:drawer.driver_picker.saved_toast',
          {
            defaultValue: isReassign ? 'Driver updated' : 'Driver assigned',
          },
        ),
      );
      onAssigned?.(result as Order);
      onClose();
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const handleDriverPick = (driver: Driver) => {
    setPickedDriver(driver);
    if (driver.vehicle) {
      // Driver already has a bound vehicle — backend infers it on
      // the PATCH. Skip Step 2.
      setPickedVehicle(undefined);
      setStep('confirm');
    } else {
      setStep('vehicle');
    }
  };

  const handleVehiclePick = (vehicle: Vehicle) => {
    setPickedVehicle(vehicle);
    setStep('confirm');
  };

  const handleConfirm = () => {
    if (!order || !pickedDriver) return;
    const payload: OrderUpdateAPI['req'] = {
      _id: order._id,
      driver: { operation: 'add', _id: getObjectId(String(pickedDriver._id)) },
    };
    if (pickedVehicle) {
      payload.vehicle = {
        operation: 'add',
        _id: getObjectId(String(pickedVehicle._id)),
      };
    }
    mutate(payload);
  };

  const handleBack = () => {
    if (step === 'confirm') {
      // Step back into vehicle if the user went through it; else
      // back into driver (driver-with-bound-vehicle short-circuit).
      const driverHasVehicle = !!pickedDriver?.vehicle;
      setStep(driverHasVehicle ? 'driver' : 'vehicle');
      if (driverHasVehicle) setPickedDriver(undefined);
      // Keep `pickedVehicle` for the user to keep editing.
    } else if (step === 'vehicle') {
      setStep('driver');
      setPickedVehicle(undefined);
    }
  };

  const isReassign = !!order?.driver?._id;
  const stepTitleKey: Record<WizardStep, string> = {
    driver: 'm-order:drawer.driver_picker.steps.driver',
    vehicle: 'm-order:drawer.driver_picker.steps.vehicle',
    confirm: 'm-order:drawer.driver_picker.steps.confirm',
  };
  const stepTitleDefault: Record<WizardStep, string> = {
    driver: 'Driver',
    vehicle: 'Vehicle',
    confirm: 'Confirm',
  };
  const wizardSteps: WizardStep[] = ['driver', 'vehicle', 'confirm'];

  return (
    <>
      <Sheet open={isOpen} onOpenChange={open => !open && onClose()}>
        <SheetContent
          side="right"
          className="flex flex-col p-0 bg-white w-[480px] sm:max-w-[480px]">
          <SheetHeader className="border-b px-6 py-4">
            <SheetTitle>
              {t(
                isReassign
                  ? 'm-order:drawer.driver_picker.title_reassign'
                  : 'm-order:drawer.driver_picker.title',
                {
                  defaultValue: isReassign ? 'Replace driver' : 'Assign driver',
                },
              )}
            </SheetTitle>
            <SheetDescription>
              {t('m-order:drawer.driver_picker.description', {
                defaultValue:
                  'Pick a driver from your fleet. We\u2019ll auto-bind their vehicle, or ask you for one if they don\u2019t have one yet.',
              })}
            </SheetDescription>
          </SheetHeader>

          {/* Stepper */}
          <div className="flex items-center gap-2 px-5 py-3 border-b">
            {wizardSteps.map((s, i) => {
              const currentIdx = wizardSteps.indexOf(step);
              const isCurrent = step === s;
              const isDone = i < currentIdx;
              return (
                <React.Fragment key={s}>
                  <div
                    className={cn(
                      'flex items-center gap-1.5 text-xs font-medium',
                      isCurrent
                        ? 'text-primary'
                        : isDone
                        ? 'text-success-400'
                        : 'text-muted-foreground',
                    )}>
                    <span className="w-5 h-5 rounded-full border flex items-center justify-center text-[10px]">
                      {i + 1}
                    </span>
                    {t(stepTitleKey[s], { defaultValue: stepTitleDefault[s] })}
                  </div>
                  {i < wizardSteps.length - 1 && (
                    <div className="flex-1 h-px bg-gray-200" />
                  )}
                </React.Fragment>
              );
            })}
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto">
            {step === 'driver' && (
              <DriverListStep
                order={order}
                onPick={handleDriverPick}
                onCreate={() => setIsCreateDriverOpen(true)}
                disabled={isPending}
                isOpen={isOpen}
              />
            )}
            {step === 'vehicle' && pickedDriver && (
              <VehicleListStep
                pickedDriver={pickedDriver}
                onPick={handleVehiclePick}
                onCreate={() => setIsCreateVehicleOpen(true)}
                disabled={isPending}
                isOpen={isOpen}
              />
            )}
            {step === 'confirm' && pickedDriver && order && (
              <ConfirmStep
                order={order}
                pickedDriver={pickedDriver}
                pickedVehicle={pickedVehicle}
              />
            )}
          </div>

          {/* Footer */}
          <div className="border-t p-4 flex items-center justify-between gap-2">
            {step === 'driver' && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setIsCreateDriverOpen(true)}
                disabled={isPending}
                className="text-primary">
                <PlusIcon className="w-4 h-4 me-1" />
                {t('m-order:drawer.driver_picker.create_new', {
                  defaultValue: 'Create new driver',
                })}
              </Button>
            )}
            {step === 'vehicle' && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setIsCreateVehicleOpen(true)}
                disabled={isPending}
                className="text-primary">
                <PlusIcon className="w-4 h-4 me-1" />
                {t('m-order:drawer.vehicle_picker.create_new', {
                  defaultValue: 'Create new vehicle',
                })}
              </Button>
            )}

            <div className="flex items-center gap-2 ms-auto">
              {step !== 'driver' && (
                <Button
                  variant="outline"
                  onClick={handleBack}
                  disabled={isPending}>
                  <ArrowLeftIcon className="w-4 h-4 me-1 rtl:rotate-180" />
                  {t('c-main:crud_buttons.back', { defaultValue: 'Back' })}
                </Button>
              )}
              {step !== 'confirm' && (
                <Button variant="outline" onClick={onClose} disabled={isPending}>
                  {t('c-main:crud_buttons.cancel', { defaultValue: 'Cancel' })}
                </Button>
              )}
              {step === 'confirm' && (
                <ConfirmCTA
                  order={order}
                  pickedDriver={pickedDriver}
                  pickedVehicle={pickedVehicle}
                  isPending={isPending}
                  onClick={handleConfirm}
                />
              )}
            </div>
          </div>
        </SheetContent>
      </Sheet>

      <DriverCreateModal
        isOpen={isCreateDriverOpen}
        onClose={() => setIsCreateDriverOpen(false)}
        onDriverSaved={driver => {
          setIsCreateDriverOpen(false);
          handleDriverPick(driver);
        }}
      />

      <VehicleEditModal
        isOpen={isCreateVehicleOpen}
        onClose={() => setIsCreateVehicleOpen(false)}
        onVehicleSaved={vehicle => {
          setIsCreateVehicleOpen(false);
          handleVehiclePick(vehicle);
        }}
      />
    </>
  );
}

// ----------------------------------------------------------------
// Step 1 — driver list
// ----------------------------------------------------------------
function DriverListStep({
  order,
  onPick,
  onCreate,
  disabled,
  isOpen,
}: {
  order: Order | null;
  onPick: (driver: Driver) => void;
  onCreate: () => void;
  disabled: boolean;
  isOpen: boolean;
}) {
  const { t, i18n } = useTranslation(['m-order', 'm-driver', 'm-vehicle', 'c-main']);
  const dir = i18n.dir();
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [tab, setTab] = useState<'matching' | 'all'>('matching');

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(query.trim()), 250);
    return () => clearTimeout(timer);
  }, [query]);

  const orderId = order?._id;
  const bestMatchQuery = useBestMatchVehiclesApi(
    { orderId: orderId! },
    { enabled: isOpen && !!orderId },
  );

  const { data, isLoading, isFetchingNextPage, hasNextPage, onLoadMore } = useDriverListApi(
    debounced ? { search: debounced } : {},
    { enabled: isOpen },
  );

  const allDrivers = useMemo<Driver[]>(
    () => (data ?? []).filter((d): d is Driver => !!d),
    [data],
  );

  const matchingDrivers = useMemo(() => {
    const bestVehicles = bestMatchQuery.data ?? [];
    const driverIdsFromVehicles = new Set(
      bestVehicles
        .map(v => (v.driver?.driverId ? String(v.driver.driverId) : undefined))
        .filter((id): id is string => !!id),
    );
    const fromBestMatch = allDrivers.filter(d => driverIdsFromVehicles.has(String(d._id)));
    const activeWithVehicle = allDrivers.filter(
      d => d.status === 'active' && !!d.vehicle && !driverIdsFromVehicles.has(String(d._id)),
    );
    const merged = [...fromBestMatch, ...activeWithVehicle];
    // De-dupe while preserving rank.
    const seen = new Set<string>();
    return merged.filter(d => {
      const id = String(d._id);
      if (seen.has(id)) return false;
      seen.add(id);
      return true;
    });
  }, [allDrivers, bestMatchQuery.data]);

  useEffect(() => {
    if (
      orderId &&
      !bestMatchQuery.isLoading &&
      matchingDrivers.length === 0 &&
      !debounced
    ) {
      setTab('all');
    }
  }, [orderId, bestMatchQuery.isLoading, matchingDrivers.length, debounced]);

  const drivers =
    tab === 'matching' && !debounced && orderId ? matchingDrivers : allDrivers;
  const showMatchingTab = !!orderId;

  return (
    <>
      {showMatchingTab && (
        <div className="px-6 pt-3">
          <div className="flex rounded-lg bg-gray-100 p-0.5 gap-0.5">
            {(['matching', 'all'] as const).map(t_tab => (
              <button
                key={t_tab}
                type="button"
                onClick={() => setTab(t_tab)}
                className={cn(
                  'flex-1 text-xs font-medium py-1.5 rounded-md transition-colors',
                  tab === t_tab
                    ? 'bg-white text-primary shadow-sm'
                    : 'text-muted-foreground hover:text-gray-700',
                )}>
                {t(
                  t_tab === 'matching'
                    ? 'm-order:drawer.driver_picker.tab_matching'
                    : 'm-order:drawer.driver_picker.tab_all',
                  {
                    defaultValue: t_tab === 'matching' ? 'Matching' : 'All',
                  },
                )}
                {t_tab === 'matching' && matchingDrivers.length > 0 && (
                  <span className="ms-1 text-[10px] text-muted-foreground">
                    ({matchingDrivers.length})
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="px-6 py-3 border-b">
        <div className="relative">
          <SearchIcon className="absolute start-3 top-1/2 -translate-y-1/2 size-4 text-gray-400" />
          <input
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder={t('m-order:drawer.driver_picker.search_placeholder', {
              defaultValue: 'Search drivers by name or phone\u2026',
            })}
            className="w-full ps-9 pe-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
          />
        </div>
      </div>

      {isLoading && drivers.length === 0 ? (
        <div className="p-6 text-center text-sm text-gray-500">
          {t('c-main:async_operations.loading', { defaultValue: 'Loading\u2026' })}
        </div>
      ) : drivers.length === 0 ? (
        <div className="p-8 flex flex-col items-center text-center gap-3">
          <div className="flex size-10 items-center justify-center rounded-full bg-gray-100">
            <DriverIcon className="size-5 text-gray-400" size="small" />
          </div>
          <div className="text-sm text-gray-500">
            {debounced
              ? t('c-main:status.no_results_found', { defaultValue: 'No results found' })
              : t('m-driver:selector.no_drivers', { defaultValue: 'No drivers found' })}
          </div>
          <Button type="button" variant="outline" size="sm" onClick={onCreate}>
            <PlusIcon className="w-4 h-4 me-1" />
            {t('m-order:drawer.driver_picker.create_new', {
              defaultValue: 'Create new driver',
            })}
          </Button>
        </div>
      ) : (
        <ul className="divide-y">
          {drivers.map(driver => {
            const vehicle = driver.vehicle;
            const chip = driverStatusChip(t, driver.status);
            const vehicleLabel = vehicle
              ? `${formatPlateForDisplay(vehicle.plate)} \u00b7 ${t(
                  `m-vehicle:types.${vehicle.type}`,
                  { defaultValue: String(vehicle.type) },
                )}`
              : t('m-driver:selector.no_vehicle', {
                  defaultValue: 'No vehicle assigned',
                });

            return (
              <li key={driver._id.toString()}>
                <button
                  type="button"
                  disabled={disabled}
                  onClick={() => onPick(driver)}
                  className="w-full text-start px-6 py-3 hover:bg-gray-50 focus:bg-gray-50 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed">
                  <div dir={dir} className="flex items-start gap-3 min-w-0">
                    <div className="flex size-9 shrink-0 items-center justify-center rounded-full border border-gray-200 bg-primary/10">
                      <DriverIcon className="size-5 text-primary" size="small" />
                    </div>
                    <div className="min-w-0 flex-1 flex flex-col gap-0.5">
                      <span className="text-sm font-semibold text-gray-900 truncate">
                        {driver.name}
                      </span>
                      <span className="text-xs text-gray-500 truncate">
                        <span dir="ltr">{driver.phone}</span>
                      </span>
                      <div
                        className={cn(
                          'flex min-w-0 items-center gap-2 text-xs',
                          vehicle ? 'text-gray-500' : 'text-red-500',
                        )}>
                        <span
                          className={cn(
                            'flex size-5 shrink-0 items-center justify-center rounded',
                            vehicle ? 'bg-gray-100' : 'bg-red-50',
                          )}>
                          <TruckIcon
                            className={cn(
                              'size-3',
                              vehicle ? 'text-gray-400' : 'text-red-500',
                            )}
                          />
                        </span>
                        <span className="truncate">{vehicleLabel}</span>
                        {!vehicle && (
                          <span className="ms-auto inline-flex items-center rounded-full border border-red-200 bg-red-50 px-2 py-0.5 text-[11px] font-medium text-red-600">
                            {t('m-order:drawer.driver_picker.assign_vehicle_cta', {
                              defaultValue: 'Assign vehicle',
                            })}
                          </span>
                        )}
                      </div>
                    </div>
                    <AvailabilityChip label={chip.label} tone={chip.tone} />
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {hasNextPage && tab === 'all' && (
        <div className="p-3 text-center">
          <Button
            variant="outline"
            size="sm"
            onClick={() => !isFetchingNextPage && onLoadMore()}
            disabled={isFetchingNextPage}>
            {isFetchingNextPage
              ? t('c-main:async_operations.loading_more', {
                  defaultValue: 'Loading more\u2026',
                })
              : t('c-main:status.load_more', { defaultValue: 'Load more' })}
          </Button>
        </div>
      )}
    </>
  );
}

// ----------------------------------------------------------------
// Step 2 — vehicle list (only reachable when picked driver has no
// bound vehicle)
// ----------------------------------------------------------------
function VehicleListStep({
  pickedDriver,
  onPick,
  onCreate,
  disabled,
  isOpen,
}: {
  pickedDriver: Driver;
  onPick: (vehicle: Vehicle) => void;
  onCreate: () => void;
  disabled: boolean;
  isOpen: boolean;
}) {
  const { t } = useTranslation(['m-order', 'm-vehicle', 'c-main']);
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(query.trim()), 250);
    return () => clearTimeout(timer);
  }, [query]);

  const { data, isLoading, isFetchingNextPage, hasNextPage, onLoadMore } = useVehicleListApi(
    debounced ? { search: debounced } : {},
    { enabled: isOpen },
  );

  const vehicles = useMemo<Vehicle[]>(
    () => (data ?? []).filter((v): v is Vehicle => !!v),
    [data],
  );

  return (
    <>
      <div className="px-6 py-3 border-b">
        <p className="text-xs text-gray-500 mb-2">
          {t('m-order:drawer.driver_picker.vehicle_step_description', {
            driverName: pickedDriver.name,
            defaultValue:
              '{{driverName}} has no vehicle bound yet. Pick one for this order.',
          })}
        </p>
        <div className="relative">
          <SearchIcon className="absolute start-3 top-1/2 -translate-y-1/2 size-4 text-gray-400" />
          <input
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder={t('m-order:drawer.vehicle_picker.search_placeholder', {
              defaultValue: 'Search vehicles by plate or type\u2026',
            })}
            className="w-full ps-9 pe-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
          />
        </div>
      </div>

      {isLoading && vehicles.length === 0 ? (
        <div className="p-6 text-center text-sm text-gray-500">
          {t('c-main:async_operations.loading', { defaultValue: 'Loading\u2026' })}
        </div>
      ) : vehicles.length === 0 ? (
        <div className="p-8 flex flex-col items-center text-center gap-3">
          <div className="flex size-10 items-center justify-center rounded-full bg-gray-100">
            <TruckIcon className="size-5 text-gray-400" />
          </div>
          <div className="text-sm text-gray-500">
            {debounced
              ? t('c-main:status.no_results_found', { defaultValue: 'No results found' })
              : t('m-vehicle:no_vehicles', { defaultValue: 'No vehicles found' })}
          </div>
          <Button type="button" variant="outline" size="sm" onClick={onCreate}>
            <PlusIcon className="w-4 h-4 me-1" />
            {t('m-order:drawer.vehicle_picker.create_new', {
              defaultValue: 'Create new vehicle',
            })}
          </Button>
        </div>
      ) : (
        <ul className="divide-y">
          {vehicles.map(vehicle => {
            const chip = vehicleStatusChip(t, vehicle.status);
            const plate = formatPlateForDisplay(vehicle.registration?.plate);
            const typeLabel = vehicle.classification?.type
              ? t(`m-vehicle:types.${vehicle.classification.type}`, {
                  defaultValue: String(vehicle.classification.type),
                })
              : '';
            return (
              <li key={vehicle._id.toString()}>
                <button
                  type="button"
                  disabled={disabled}
                  onClick={() => onPick(vehicle)}
                  className="w-full text-start px-6 py-3 hover:bg-gray-50 focus:bg-gray-50 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed">
                  <div className="flex items-start gap-3">
                    <div className="flex size-9 shrink-0 items-center justify-center rounded-full bg-primary/10 border border-gray-200">
                      <TruckIcon className="size-4 text-primary" />
                    </div>
                    <div className="min-w-0 flex-1 flex flex-col gap-0.5">
                      <span className="text-sm font-semibold text-gray-900 truncate">
                        {plate || t('m-vehicle:no_plate', { defaultValue: 'No plate' })}
                      </span>
                      {typeLabel && (
                        <span className="text-xs text-gray-500 truncate">
                          {typeLabel}
                        </span>
                      )}
                    </div>
                    <AvailabilityChip label={chip.label} tone={chip.tone} />
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {hasNextPage && (
        <div className="p-3 text-center">
          <Button
            variant="outline"
            size="sm"
            onClick={() => !isFetchingNextPage && onLoadMore()}
            disabled={isFetchingNextPage}>
            {isFetchingNextPage
              ? t('c-main:async_operations.loading_more', {
                  defaultValue: 'Loading more\u2026',
                })
              : t('c-main:status.load_more', { defaultValue: 'Load more' })}
          </Button>
        </div>
      )}
    </>
  );
}

// ----------------------------------------------------------------
// Step 3 — confirm. First-assign shows a single review card; reassign
// shows a "current → new" swap card. Busy badges + warning when the
// pick is currently on another order.
// ----------------------------------------------------------------
type DriverSlim = { _id: string; name: string; phone?: string; statusBusy?: boolean };
type VehicleSlim = { _id: string; plateLabel: string; typeLabel: string; statusBusy?: boolean };

function ConfirmStep({
  order,
  pickedDriver,
  pickedVehicle,
}: {
  order: Order;
  pickedDriver: Driver;
  pickedVehicle: Vehicle | undefined;
}) {
  const { t } = useTranslation(['m-order', 'm-driver', 'm-vehicle', 'c-main']);

  const currentDriver: DriverSlim | undefined = order.driver
    ? {
        _id: String(order.driver._id),
        name: order.driver.name ?? '',
        phone: order.driver.phone,
        // Status not snapshotted on Order.driver — the chip lives
        // only on the picked driver / vehicle objects.
      }
    : undefined;

  const newDriver: DriverSlim = {
    _id: String(pickedDriver._id),
    name: pickedDriver.name,
    phone: pickedDriver.phone,
    statusBusy: pickedDriver.status === 'busy',
  };

  // Vehicle resolution:
  //   - Explicit pick from Step 2 wins.
  //   - Otherwise fall back to driver's bound vehicle snapshot
  //     (`Driver.vehicle` is a flat-shape selector with `plate` and
  //     `type` directly, NOT `registration.plate` / `classification.type`).
  const inferredFromDriver = pickedDriver.vehicle;
  const newVehicle: VehicleSlim | undefined = pickedVehicle
    ? {
        _id: String(pickedVehicle._id),
        plateLabel:
          formatPlateForDisplay(pickedVehicle.registration?.plate) ||
          t('m-vehicle:no_plate', { defaultValue: 'No plate' }),
        typeLabel: pickedVehicle.classification?.type
          ? t(`m-vehicle:types.${pickedVehicle.classification.type}`, {
              defaultValue: String(pickedVehicle.classification.type),
            })
          : '',
        statusBusy: pickedVehicle.status === 'busy',
      }
    : inferredFromDriver
    ? {
        _id: String(inferredFromDriver._id),
        plateLabel:
          formatPlateForDisplay(inferredFromDriver.plate) ||
          t('m-vehicle:no_plate', { defaultValue: 'No plate' }),
        typeLabel: inferredFromDriver.type
          ? t(`m-vehicle:types.${inferredFromDriver.type}`, {
              defaultValue: String(inferredFromDriver.type),
            })
          : '',
        statusBusy: inferredFromDriver.status === 'busy',
      }
    : undefined;

  const currentVehicle: VehicleSlim | undefined = order.vehicle
    ? {
        _id: String(order.vehicle._id),
        plateLabel:
          formatPlateForDisplay(order.vehicle.registration?.plate) ||
          t('m-vehicle:no_plate', { defaultValue: 'No plate' }),
        typeLabel: order.vehicle.classification?.type
          ? t(`m-vehicle:types.${order.vehicle.classification.type}`, {
              defaultValue: String(order.vehicle.classification.type),
            })
          : '',
      }
    : undefined;

  const isReassignDriver =
    !!currentDriver && !isIdMatch(currentDriver._id, newDriver._id);
  const isReassignVehicle =
    !!currentVehicle && !!newVehicle && !isIdMatch(currentVehicle._id, newVehicle._id);

  const showDriverBusy = newDriver.statusBusy;
  const showVehicleBusy = newVehicle?.statusBusy;

  return (
    <div className="p-6 space-y-4">
      {/* Driver card */}
      <SwapCard
        kind="driver"
        title={t('m-order:drawer.driver_picker.confirm.driver_label', {
          defaultValue: 'Driver',
        })}
        currentName={currentDriver?.name}
        newName={newDriver.name}
        newSubtitle={newDriver.phone}
        showSwapArrow={isReassignDriver}
        isBusy={!!showDriverBusy}
      />

      {/* Vehicle card (only when we have a vehicle to show) */}
      {newVehicle && (
        <SwapCard
          kind="vehicle"
          title={t('m-order:drawer.driver_picker.confirm.vehicle_label', {
            defaultValue: 'Vehicle',
          })}
          currentName={currentVehicle?.plateLabel}
          currentSubtitle={currentVehicle?.typeLabel}
          newName={newVehicle.plateLabel}
          newSubtitle={newVehicle.typeLabel}
          showSwapArrow={isReassignVehicle}
          isBusy={!!showVehicleBusy}
        />
      )}

      {(showDriverBusy || showVehicleBusy) && (
        <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          <AlertCircleIcon className="size-4 shrink-0 mt-0.5" />
          <span>
            {showDriverBusy
              ? t('m-order:drawer.driver_picker.confirm.busy_warning', {
                  defaultValue:
                    'This driver is currently on another order. They will keep both.',
                })
              : t('m-order:drawer.driver_picker.confirm.busy_warning_vehicle', {
                  defaultValue:
                    'This vehicle is currently on another order. It will keep both.',
                })}
          </span>
        </div>
      )}
    </div>
  );
}

function SwapCard({
  kind,
  title,
  currentName,
  currentSubtitle,
  newName,
  newSubtitle,
  showSwapArrow,
  isBusy,
}: {
  kind: 'driver' | 'vehicle';
  title: string;
  currentName?: string;
  currentSubtitle?: string;
  newName: string;
  newSubtitle?: string;
  showSwapArrow: boolean;
  isBusy: boolean;
}) {
  const { t } = useTranslation(['m-driver', 'm-vehicle']);
  const Icon = kind === 'driver' ? DriverIcon : TruckIcon;
  const busyLabel =
    kind === 'driver'
      ? t('m-driver:status.busy', { defaultValue: 'Busy' })
      : t('m-vehicle:status.busy', { defaultValue: 'Busy' });

  return (
    <div className="rounded-md border border-gray-200 p-3 bg-white">
      <div className="text-xs font-medium text-gray-500 mb-2">{title}</div>
      <div className="flex items-center gap-2">
        {showSwapArrow && currentName && (
          <>
            <EntityChip
              icon={<Icon className="size-4" size="small" />}
              name={currentName}
              subtitle={currentSubtitle}
              dim
            />
            <ArrowRightIcon className="size-4 text-gray-400 rtl:rotate-180" />
          </>
        )}
        <EntityChip
          icon={<Icon className="size-4 text-primary" size="small" />}
          name={newName}
          subtitle={newSubtitle}
          highlight
          badge={isBusy ? busyLabel : undefined}
        />
      </div>
    </div>
  );
}

function EntityChip({
  icon,
  name,
  subtitle,
  dim,
  highlight,
  badge,
}: {
  icon: React.ReactNode;
  name: string;
  subtitle?: string;
  dim?: boolean;
  highlight?: boolean;
  badge?: string;
}) {
  return (
    <div
      className={cn(
        'flex-1 min-w-0 flex items-center gap-2 rounded-md border px-2.5 py-2',
        dim && 'border-gray-200 bg-gray-50 text-gray-600',
        highlight && 'border-primary/40 bg-primary/5',
      )}>
      <div className="flex size-7 shrink-0 items-center justify-center rounded-full bg-white border border-gray-200">
        {icon}
      </div>
      <div className="min-w-0 flex-1 flex flex-col">
        <span className="text-sm font-semibold truncate">{name}</span>
        {subtitle && (
          <span className="text-xs text-gray-500 truncate">
            <span dir="ltr">{subtitle}</span>
          </span>
        )}
      </div>
      {badge && (
        <span className="inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-800">
          {badge}
        </span>
      )}
    </div>
  );
}

function ConfirmCTA({
  order,
  pickedDriver,
  pickedVehicle,
  isPending,
  onClick,
}: {
  order: Order | null;
  pickedDriver: Driver | undefined;
  pickedVehicle: Vehicle | undefined;
  isPending: boolean;
  onClick: () => void;
}) {
  const { t } = useTranslation(['m-order', 'c-main']);
  if (!order || !pickedDriver) return null;

  const isReassign = !!order.driver?._id;
  const driverUnchanged = isReassign && isIdMatch(order.driver?._id, pickedDriver._id);
  const vehicleUnchanged =
    !pickedVehicle || (order.vehicle && isIdMatch(order.vehicle._id, pickedVehicle._id));
  const noOp = driverUnchanged && vehicleUnchanged;

  return (
    <Button onClick={onClick} disabled={isPending || noOp}>
      {isPending && <Spinner className="size-4 me-2" />}
      {t(
        isReassign
          ? 'm-order:drawer.driver_picker.confirm.cta_replace'
          : 'm-order:drawer.driver_picker.confirm.cta_assign',
        {
          defaultValue: isReassign ? 'Replace' : 'Assign',
        },
      )}
    </Button>
  );
}
